import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckIcon from "@mui/icons-material/Check";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import FlightIcon from "@mui/icons-material/Flight";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import HotelIcon from "@mui/icons-material/Hotel";
import LightModeIcon from "@mui/icons-material/LightMode";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import StarPurple500Icon from "@mui/icons-material/StarPurple500";
import ThumbUpAltSharpIcon from "@mui/icons-material/ThumbUpAltSharp";
import {
  AppBar,
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemIcon,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Slide,
  Toolbar,
  Tooltip,
  Typography,
  useScrollTrigger,
} from "@mui/material";

import ToobarOffset from "@/components/ToobarOffset";
import { siteName } from "@/constants/config";
import ColorModeContext from "@/context/ColorModeContext";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useLayoutData from "@/hooks/useLayoutData";
import usePlatform from "@/hooks/usePlatform";
import useThemeData from "@/hooks/useThemeData";

import DrawerNavBtn from "./DrawerNavBtn";
import MobileNavBtn from "./MobileNavBtn";
import ProgramBanks from "./ProgramBanks";
import ToolBarNavBtn from "./ToolBarNavBtn";
import ToolsMenu from "./Tools";
import UserInfoMenu from "./UserInfoMenu";

// function ElevationScroll(props: { children: ReactElement }) {
//   const { children } = props;

//   const trigger = useScrollTrigger({
//     disableHysteresis: true,
//     threshold: 0,
//   });
//   return React.cloneElement(children, {
//     elevation: trigger ? 4 : 1,
//     sx: {
//       backgroundImage: "linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))",
//     },
//   });
// }

interface Props {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window?: () => Window;
  children?: React.ReactElement<unknown>;
}

function HideOnScroll(props: Props) {
  const { children, window } = props;
  // Note that you normally won't need to set the window ref as useScrollTrigger
  // will default to window.
  // This is only being set here because the demo is in an iframe.
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
    disableHysteresis: true,
    threshold: 350,
  });
  const { isPc } = usePlatform();
  return isPc ? (
    <Slide appear={false} direction="down" in={!trigger}>
      {children ?? <div />}
    </Slide>
  ) : (
    <Slide appear direction="down" in>
      {children ?? <div />}
    </Slide>
  );
}

const AppPageBar = () => {
  const { isPc } = usePlatform();
  const pages = useMemo(() => {
    return [
      {
        name: "Pro",
        path: "/pro",
        matches: ["/pro"],
        icon: <ThumbUpAltSharpIcon />,
      },
      {
        name: "Flights",
        path: "/",
        matches: ["/", "/flight"],
        icon: <FlightIcon className="rotate-[45deg]" />,
      },
      {
        name: "Panorama",
        path: "/routes",
        matches: ["/routes", "/routes-flights"],
        icon: <StarPurple500Icon />,
        specialIcon: <StarPurple500Icon fontSize="small" />,
      },
      {
        name: "Deals",
        path: "/deals/routes",
        matches: ["/deals/routes"],
        icon: <ExploreOutlinedIcon />,
        specialIcon: <ExploreOutlinedIcon />,
      },
      {
        name: "Routes",
        path: "/all-routes",
        matches: ["/all-routes", "/route-detail"],
        icon: <CalendarMonthIcon />,
        specialIcon: <CalendarMonthIcon fontSize="small" />,
      },
      {
        name: "Hotels",
        path: "/hotels",
        matches: ["/hotels", "/hotel/details"],
        icon: <HotelIcon />,
        specialIcon: <HotelIcon />,
      },
      // {
      //   name: "Mega",
      //   path: "/mega",
      //   matches: ["/mega"],
      //   icon: <TravelExploreIcon />,
      // },
      {
        name: "Alerts",
        path: "/alert",
        matches: ["/alert", "/alert-detail"],
        icon: <NotificationsIcon />,
        specialIcon: <NotificationsIcon />,
      },
      // {
      //   name: "Cards",
      //   path: "/credit-card",
      //   matches: ["/credit-card"],
      //   icon: <CreditCardIcon />,
      // },
      {
        name: "Tools",
        // path: "/live-seat-map",
        matches: ["/live-seat-map", "/guides", "/deals/flight"],
        icon: <HomeRepairServiceIcon />,
        specialIcon: <HomeRepairServiceIcon fontSize="small" />,
        action: "dialog",
      },
    ];
  }, []);

  const themeOptions = useMemo(() => {
    return [
      { label: "Use device default", value: "" },
      { label: "Dark theme", value: "dark" },
      { label: "Light theme", value: "light" },
    ];
  }, []);

  const colorMode = useContext(ColorModeContext);
  const theme = useThemeData();
  const navigator = useNavigate();
  const { showMobileNav } = useLayoutData();

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [dropDownType, setDropDownType] = useState<string>("");

  const timer = useRef<NodeJS.Timeout | null>(null);

  const [visible, setVisible] = useState(false);

  const [currentColorMode, setCurrentColorMode] = useState(
    localStorage.getItem("color-mode") || ""
  );

  const store = useGlobalStoreContext();
  const [navOpen, setNavOpen] = React.useState(false);
  const [anchorElColorMode, setAnchorElColorMode] = React.useState<null | HTMLElement>(null);

  const handleOpenNavMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setNavOpen((prev) => !prev);
  }, []);

  const handleCloseNavMenu = useCallback(() => {
    setNavOpen(false);
  }, []);

  const handleOpenAppearanceMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorElColorMode(event.currentTarget);
  }, []);

  const handleAppearanceChange = useCallback(
    (value: string) => {
      setCurrentColorMode(value);
      setAnchorElColorMode(null);
      colorMode.toggleColorMode(value);
    },
    [colorMode]
  );

  const handleNavClick = useCallback(
    (path: string) => {
      navigator(path);

      setNavOpen(false);
    },
    [navigator]
  );

  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>, type: string) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);

    setDropDownType(type);
  };

  const handleMouseLeave = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!event.relatedTarget || !event.relatedTarget?.closest?.("#simple-popper")) {
      timer.current = setTimeout(() => {
        setOpen(false);

        setDropDownType("");
      }, 100);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const id = open ? "simple-popper" : undefined;

  return (
    <>
      <HideOnScroll>
        <AppBar
          onClick={() => setNavOpen(false)}
          color="primary"
          elevation={0}
          sx={{
            borderBottom: theme.isDarkMode ? "1px solid #5f6368" : "1px solid #dadce0",
          }}
        >
          <Toolbar>
            <IconButton
              size="large"
              edge="start"
              sx={{ marginRight: { xs: "2px", md: "5px" }, color: "#fff" }}
              onClick={handleOpenNavMenu}
            >
              <MenuIcon />
            </IconButton>

            <div
              className="mr-[12px] cursor-pointer"
              onClick={() => {
                window.location.href = "/";
              }}
            >
              <Typography
                variant="h6"
                noWrap
                // color="primary"
                sx={{
                  fontWeight: 500,
                  letterSpacing: ".1rem",
                  color: "#fff",
                  textDecoration: "none",
                  lineHeight: 1.3,
                }}
              >
                {siteName}
                {store.persistStore?.show_upsell !== undefined &&
                !store.persistStore?.show_upsell ? (
                  <Typography
                    // noWrap
                    className="inline"
                    sx={{
                      fontFamily: 'Roboto, "Helvetica Neue", sans-serif', // Added Roboto as the primary font
                      fontSize: 11,
                      fontWeight: !1000,
                      borderColor: "white",
                      borderWidth: "1px",
                      borderRadius: "3px",
                      padding: "1px",
                      paddingX: "4px",
                      borderStyle: "solid",
                      color: "#fff",
                      textDecoration: "none",
                      letterSpacing: ".05rem",
                      marginLeft: "5.5px",
                      position: "relative",
                      top: "-3px", // Adjust this value to move the text up
                      // textShadow: "1px 1px 1px", // Adding a subtle shadow for emphasis
                      // marginBottom: "90px",
                      // display: "absolute",
                    }}
                  >
                    PRO
                  </Typography>
                ) : (
                  ""
                )}
              </Typography>
              <Typography
                noWrap
                sx={{
                  color: "#fff",
                  fontSize: 12.5,
                  lineHeight: 1,
                }}
              >
                {isPc ? "Save thousands and hours" : "Save thousands and hours"}
              </Typography>
            </div>

            <Box
              key={`${theme.isDarkMode}`}
              sx={{
                flexGrow: 0,
                display: { xs: "none", md: "none", lg: "flex" },
                position: "relative",
              }}
            >
              {pages.map((page) => {
                if (["Routes", "Tools"].includes(page.name)) {
                  return (
                    <ToolBarNavBtn
                      aria-describedby={page.name}
                      key={page.path}
                      data={page}
                      onClick={(e) => {
                        handleMouseEnter(e, page.name);
                        handleNavClick(page.path);
                        setDropDownType(page.name);
                      }}
                      forceActive={open && dropDownType === page.name}
                      onMouseEnter={(e) => {
                        handleMouseEnter(e, page.name);
                      }}
                      onMouseLeave={handleMouseLeave}
                    />
                  );
                } else if (page.name === "Panorama") {
                  return (
                    <ToolBarNavBtn
                      aria-describedby={id}
                      key={page.path}
                      data={page}
                      onClick={() => {
                        if (localStorage.getItem("show_individual_flights") === "true") {
                          navigator("/routes-flights");
                        } else {
                          navigator(page.path);
                        }
                      }}
                    />
                  );
                } else {
                  return (
                    <ToolBarNavBtn
                      key={page.path}
                      data={page}
                      onClick={() => handleNavClick(page.path)}
                    />
                  );
                }
              })}

              <Popper
                id={id}
                open={open}
                anchorEl={anchorEl}
                placement="bottom-start"
                onMouseLeave={handleClose}
                sx={{ zIndex: 1200 }}
                onMouseEnter={() => {
                  if (timer.current) {
                    clearTimeout(timer.current);
                  }
                }}
              >
                {() => (
                  <ClickAwayListener onClickAway={handleClose}>
                    <Paper
                      sx={{
                        maxHeight: "calc(100vh - 64px)",
                        overflowY: "auto",
                        marginTop: "6px",
                      }}
                    >
                      {dropDownType === "Routes" && <ProgramBanks onClose={handleClose} />}

                      {dropDownType === "Tools" && <ToolsMenu onClose={handleClose} />}
                    </Paper>
                  </ClickAwayListener>
                )}
              </Popper>
            </Box>

            <Box className="flex flex-1 justify-end items-center">
              <Tooltip title="Change appearance" enterDelay={500}>
                <IconButton onClick={handleOpenAppearanceMenu}>
                  {theme.isDarkMode ? (
                    <LightModeIcon fontSize="small" />
                  ) : (
                    <DarkModeIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>

              <Menu
                anchorEl={anchorElColorMode}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorElColorMode)}
                onClose={() => setAnchorElColorMode(null)}
              >
                <MenuItem sx={{ minHeight: "36px" }}>
                  <ListItemIcon />
                  <Typography variant="body2" color="textSecondary">
                    Appearance
                  </Typography>
                </MenuItem>
                <Divider />
                {themeOptions.map((theme, i) => (
                  <MenuItem key={`${i}`} onClick={() => handleAppearanceChange(theme.value)}>
                    <ListItemIcon>
                      {currentColorMode === theme.value ? <CheckIcon fontSize="small" /> : null}
                    </ListItemIcon>
                    <Typography variant="body1">{theme.label}</Typography>
                  </MenuItem>
                ))}
              </Menu>

              <UserInfoMenu />
            </Box>
          </Toolbar>

          {showMobileNav ? (
            <Box className="flex !mt-[-3px] pl-[6px] pr-[12px] overflow-x-auto overflow-y-hidden no-scrollbar">
              {pages.map((page, index) => (
                <MobileNavBtn
                  key={`${index}`}
                  data={page}
                  onClick={(path: string) => {
                    handleNavClick(path);
                    if (page.action === "dialog") {
                      setVisible(true);
                    }
                  }}
                />
              ))}
            </Box>
          ) : null}
        </AppBar>
      </HideOnScroll>
      <ToobarOffset />

      <Drawer
        variant="temporary"
        open={navOpen}
        onClose={handleCloseNavMenu}
        ModalProps={{ keepMounted: true }}
        sx={{
          zIndex: 1000,
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: 250 },
          "& .MuiBackdrop-root": { backgroundColor: "transparent" },
        }}
      >
        <ToobarOffset />
        <Box sx={{ flex: 1, flexDirection: "column" }}>
          <List>
            {pages.map((page, index) => (
              <DrawerNavBtn key={`${index}`} data={page} onClick={handleNavClick} />
            ))}
          </List>
          <Divider />
        </Box>
      </Drawer>

      <Dialog open={visible} fullScreen onClose={() => setVisible(false)}>
        <div className="px-[16px] pt-[12px] flex items-center">
          <IconButton aria-label="swap" size="small" onClick={() => setVisible(false)}>
            <ArrowBackIcon />
          </IconButton>
        </div>
        <DialogContent sx={{ padding: 0 }}>
          <ToolsMenu
            onClose={() => {
              setVisible(false);
              handleClose();
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default React.memo(AppPageBar);
